/**
 * AdminAuditLogPage — iter1018
 *
 * Super-admin viewer for impersonation audit trails.
 * Merges impersonation_audit (admin full-access) and doctor_impersonation_audit
 * (doctor read-only) into a single filterable, sortable table.
 *
 * Route: /admin/audit-log
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Loader2, Filter, ShieldCheck, ShieldAlert, Search,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAuditLogPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState(""); // "", admin, doctor
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [since, setSince] = useState("");
  const [limit] = useState(200);

  useEffect(() => {
    if (!user) return;
    if ((user.role || "").toLowerCase() !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (kind) params.set("kind", kind);
      if (actor) params.set("actor_email", actor);
      if (target) params.set("target_email", target);
      if (since) {
        try {
          const iso = new Date(since).toISOString();
          params.set("since_iso", iso);
        } catch { /* skip invalid */ }
      }
      const res = await axios.get(`${API}/admin/audit/impersonation?${params.toString()}`);
      setRows(res.data?.rows || []);
    } catch (e) {
      toast.error("Неуспешно зареждане на audit лога");
    } finally {
      setLoading(false);
    }
  }, [kind, actor, target, since, limit]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const adm = rows.filter((r) => r.kind === "admin").length;
    const doc = rows.filter((r) => r.kind === "doctor").length;
    return { adm, doc, total: rows.length };
  }, [rows]);

  const wrapCls = isDark ? "bg-[hsl(222,47%,8%)] text-gray-100" : "bg-stone-50 text-stone-900";
  const cardCls = isDark ? "bg-white/[0.04] border-white/10" : "bg-white border-stone-200";
  const inputCls = isDark
    ? "bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500"
    : "bg-white border-stone-200 text-stone-900 placeholder:text-stone-400";

  return (
    <div className={`min-h-screen ${wrapCls}`} data-testid="admin-audit-log-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-1.5 text-xs opacity-70 hover:opacity-100 mb-2"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Обратно към Админ панел
          </button>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Impersonation Audit Log</h1>
          <p className="text-sm opacity-70 mt-1 max-w-3xl">
            Пълен GDPR trail за всяко влизане в чужд акаунт — както админ full-access
            impersonation, така и doctor read-only преглед на одобрени пациенти.
          </p>
        </div>

        {/* stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="audit-stat-admin">
            <div className="text-[10px] uppercase opacity-60 font-semibold">Admin full-access</div>
            <div className="text-lg font-bold mt-0.5">{stats.adm}</div>
          </div>
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="audit-stat-doctor">
            <div className="text-[10px] uppercase opacity-60 font-semibold">Doctor read-only</div>
            <div className="text-lg font-bold mt-0.5">{stats.doc}</div>
          </div>
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="audit-stat-total">
            <div className="text-[10px] uppercase opacity-60 font-semibold">Общо</div>
            <div className="text-lg font-bold mt-0.5">{stats.total}</div>
          </div>
        </div>

        {/* filters */}
        <div className={`p-3 rounded-xl border mb-3 flex flex-wrap gap-2 items-end ${cardCls}`}>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase opacity-60 font-semibold mb-1">Тип</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className={`px-2 py-1.5 rounded-md text-sm border ${inputCls}`}
              data-testid="audit-filter-kind"
            >
              <option value="">Всички</option>
              <option value="admin">Само admin</option>
              <option value="doctor">Само doctor</option>
            </select>
          </div>
          <div className="flex flex-col flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase opacity-60 font-semibold mb-1">Actor email</label>
            <input
              type="text"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="admin@… или doctor@…"
              className={`px-2 py-1.5 rounded-md text-sm border ${inputCls}`}
              data-testid="audit-filter-actor"
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase opacity-60 font-semibold mb-1">Target email</label>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="patient@…"
              className={`px-2 py-1.5 rounded-md text-sm border ${inputCls}`}
              data-testid="audit-filter-target"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase opacity-60 font-semibold mb-1">От дата</label>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className={`px-2 py-1.5 rounded-md text-sm border ${inputCls}`}
              data-testid="audit-filter-since"
            />
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-50"
            data-testid="audit-apply-filters"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Filter className="w-3.5 h-3.5" />}
            Приложи
          </button>
          <button
            onClick={() => { setKind(""); setActor(""); setTarget(""); setSince(""); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-stone-300 dark:border-white/10"
            data-testid="audit-reset-filters"
          >
            Reset
          </button>
        </div>

        {/* table */}
        {loading ? (
          <div className={`p-8 rounded-xl border text-center text-sm opacity-70 ${cardCls}`}>
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Зарежда…
          </div>
        ) : rows.length === 0 ? (
          <div className={`p-8 rounded-xl border text-center text-sm opacity-70 ${cardCls}`}>
            Няма записи за избраните филтри.
          </div>
        ) : (
          <div className={`rounded-xl border overflow-hidden ${cardCls}`} data-testid="audit-rows">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={isDark ? "bg-white/[0.02]" : "bg-stone-100"}>
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Тип</th>
                    <th className="text-left px-3 py-2 font-semibold">Actor</th>
                    <th className="text-left px-3 py-2 font-semibold">Target</th>
                    <th className="text-left px-3 py-2 font-semibold">Кога</th>
                    <th className="text-left px-3 py-2 font-semibold">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={`${r.at}-${idx}`} className={`border-t ${isDark ? "border-white/5" : "border-stone-100"}`} data-testid={`audit-row-${idx}`}>
                      <td className="px-3 py-2">
                        {r.kind === "admin" ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold text-[10px]">
                            <ShieldAlert className="w-3 h-3" /> ADMIN
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-700 dark:text-sky-300 font-bold text-[10px]">
                            <ShieldCheck className="w-3 h-3" /> DOCTOR RO
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono truncate max-w-[180px]" title={r.actor_email}>{r.actor_email}</td>
                      <td className="px-3 py-2 font-mono truncate max-w-[180px]" title={r.target_email}>{r.target_email}</td>
                      <td className="px-3 py-2 whitespace-nowrap opacity-80">
                        {r.at ? new Date(r.at).toLocaleString("bg-BG") : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono opacity-70">{r.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
